import { Component, Input, OnChanges, OnDestroy, SimpleChanges, OnInit, Output, EventEmitter } from '@angular/core';
import {
  Destroy,
  tr,
  IQueryResult,
  DialogService,
  SearchConfig,
  Types,
  KeyBase,
  Key,
  DialogResult,
  chainAndForkJoinObservables,
  SecureActionDialogData,
  SortExpression,
} from '@kpd/framework-ngx';
import { of, Observable } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { RoleKey } from 'app/shared/models/keys/role-key';
import { User } from 'app/shared/models/user';
import { RoleService } from 'app/shared/services/role.service';
import { UserService } from 'app/shared/services/user.service';
import { Role } from 'app/shared/models/role';
import { AuthService } from '@kpd/framework-ngx';
import { RoleFacade } from 'app/shared/services/facades/role.facade';
import { Permissions } from 'app/shared/services/permissions';
import { TableController } from 'app/shared/utils/table-controller';
import { UsersNavigatorHelper } from 'app/users/users-navigator-helper';

@Component({
  selector: 'app-role-details-users',
  templateUrl: './role-details-users.component.html',
})
export class RoleDetailsUsersComponent extends TableController implements OnInit, OnChanges, OnDestroy {
  Permissions: typeof Permissions = Permissions;
  @Input()
  role: Role;
  @Output()
  switchMode = new EventEmitter();
  @Output()
  userCountChanged = new EventEmitter<number>();
  @Destroy()
  destroy$: any;

  readOnlyMode = true;

  columnsConfig = this.createColumnsConfig();
  saveErrors: string[] = [];
  linkUsersSearchConfig = this.createLinkUsersSearchConfig();
  linkUsersCols = this.createLinkUsersCols();
  canManageRole = true;
  sortExpression = new SortExpression('upn');

  get isSystemUserSelected(): boolean {
    const systemUsers = (this.items as User[]).filter((u) => u.isSystemUser);
    return (this.selectedKeys as Key<string>[]).some((k) => systemUsers.map((s) => s.key).includes(k));
  }

  get hasSystemUserManagePermission(): boolean {
    return this.authService.getIdentity().hasPermission(Permissions.SystemUserManage);
  }

  constructor(
    private roleFacade: RoleFacade,
    private roleService: RoleService,
    private userService: UserService,
    private dialogService: DialogService,
    private authService: AuthService,
    private userNavigationHelper: UsersNavigatorHelper,
  ) {
    super();
    if (this.roleFacade.roleUsersSortExpression) {
      this.sortExpression = this.roleFacade.roleUsersSortExpression;
    }
  }

  ngOnInit(): void {
    if (this.authService.getIdentity().hasPermission(Permissions.UserRoleUpdate)) {
      this.readOnlyMode = false;
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.role) {
      this.selectedKeys = [];
      this.saveErrors = [];
      if (!!changes.role.currentValue && !!changes.role.currentValue.key) {
        const roleKey = changes.role.currentValue.key;

        this.canManageRole = true;
        this.wrapLoadingSpinner(
          this.roleFacade.canManageRole(roleKey).pipe(
            tap((canManage) => (this.canManageRole = canManage)),
            switchMap(() => this.loadRoleUsersObservable(roleKey)),
          ),
        );
      }
    }
  }

  onSort(event: { sortExpression: SortExpression }): void {
    this.sortExpression = event.sortExpression;
    this.roleFacade.roleUsersSortExpression = this.sortExpression;
  }

  wrapLoadingSpinner(obs: Observable<any>): void {
    this.roleFacade.detailLoadingStarted();
    obs.subscribe(
      () => this.roleFacade.detailLoadingFinished(),
      () => this.roleFacade.detailLoadingFinished(),
    );
  }

  loadRoleUsers(roleKey: RoleKey): void {
    this.wrapLoadingSpinner(this.loadRoleUsersObservable(roleKey));
  }

  loadRoleUsersObservable(roleKey: RoleKey): Observable<User[]> {
    return this.roleService.getRoleUsers(roleKey).pipe(
      map((data) => {
        return this.sortExpression.sortItems(data);
      }),
      tap((users) => {
        this.items = users;
        this.userCountChanged.emit(users.length);
      }),
    );
  }

  navigateToUser(key: Key<string>): void {
    this.userNavigationHelper.navigateToDetail(key, false, false, false, { tab: 'general' });
  }

  onAdd(): void {
    // load initial data
    this.roleFacade.detailLoadingStarted();
    this.userService
      .get()
      .pipe(
        switchMap((queryResult: IQueryResult) => {
          const inputData: { queryResult: IQueryResult; keysToExclude: KeyBase[] } = {
            queryResult: queryResult,
            keysToExclude: this.items.map((user) => user.key),
          };
          this.roleFacade.detailLoadingFinished();
          return this.dialogService.open('linkEntities', inputData).afterClosed();
        }),
      )
      .subscribe((selectedKeys?: Key<string>[]) => {
        if (!selectedKeys) {
          return;
        }
        this.saveErrors = [];
        this.roleFacade.detailLoadingStarted();
        // re-load resource suppliers after a successful add
        const observers = selectedKeys.map((key) => {
          return this.roleService.createUserRole(this.role.key as RoleKey, key.id).pipe(
            catchError(() => {
              this.saveErrors.push(tr('Following user could not be added: ') + key.id);
              return of(null);
            }),
          );
        });
        chainAndForkJoinObservables(observers).subscribe(() => {
          this.loadRoleUsers(this.role.key as RoleKey);
          this.roleFacade.detailLoadingFinished();
        });
      });
  }

  onDelete(): void {
    if (this.isSystemUserSelected) {
      const messages = [
        {
          text: tr('Are you sure you want to remove this role from a System User?'),
        },
      ];
      const confirmData: SecureActionDialogData = {
        header: tr(`Remove {1} from {2} ?`)
          .replace('{1}', `${tr('Role')} ${this.role.name}`)
          .replace('{2}', ` ${tr('System User')}`),
        messages: messages,
        cancelButtonText: tr('Cancel'),
        verifyButtonText: tr('Remove'),
        verifyButtonStyle: 'danger',
        verificationString: this.role.name.trim(),
        verificationLabel: tr('Type the name of the {value} to confirm').replace(/{.*}/, `${tr('role')}`),
      };
      this.dialogService
        .open('secureDelete', confirmData)
        .afterClosed()
        .subscribe((result: DialogResult) => {
          if (result === DialogResult.Verify) {
            this.executeUserRoleDelete();
            return true;
          } else {
            return false;
          }
        });
      return;
    }

    this.dialogService
      .open('confirmRoleUserDelete', {
        message: tr('Are you sure you want to unassign the user(s)?'),
        header: tr('Unassign user(s)'),
        verifyButtonText: tr('Unassign'),
        verifyButtonStyle: 'danger',
        cancelButtonText: tr('Cancel'),
      })
      .afterClosed()
      .subscribe((result) => {
        this.saveErrors = [];
        if (result === DialogResult.Verify) {
          this.executeUserRoleDelete();
        }
      });
  }

  private executeUserRoleDelete() {
    this.roleFacade.detailLoadingStarted();
    const observables = this.selectedKeys.map((key) => {
      return this.roleService.deleteUserRole(this.role.key as RoleKey, key.toString()).pipe(
        catchError(() => {
          this.roleFacade.detailLoadingFinished();
          this.saveErrors.push(tr('Following user could not be deleted: ') + key.toString());
          return of(null);
        }),
      );
    });
    chainAndForkJoinObservables(observables).subscribe(() => {
      this.selectedKeys = [];
      this.loadRoleUsers(this.role.key as RoleKey);
      this.roleFacade.detailLoadingFinished();
    });
  }

  isRoleAvailable(): boolean {
    return this.role instanceof Role;
  }

  createColumnsConfig(): any[] {
    return [
      { field: 'upn', header: tr('Upn'), width: '20%' },
      { field: 'firstName', header: tr('First name'), width: '25%' },
      { field: 'lastName', header: tr('Last name'), width: '25%' },
      { field: 'displayName', header: tr('Name'), width: '30%' },
    ];
  }

  createLinkUsersSearchConfig(): SearchConfig {
    return new SearchConfig({
      multipleCriteria: true,
      fields: [
        {
          field: 'upn',
          displayValue: tr('Upn'),
          dataType: Types.String,
        },
        {
          field: 'firstName',
          displayValue: tr('First name'),
          dataType: Types.String,
        },
        {
          field: 'lastName',
          displayValue: tr('Last name'),
          dataType: Types.String,
        },
        {
          field: 'displayName',
          displayValue: tr('Name'),
          dataType: Types.String,
        },
      ],
    });
  }

  createLinkUsersCols(): any[] {
    return [
      { field: 'upn', header: tr('Upn'), width: '20%' },
      { field: 'firstName', header: tr('First name'), width: '25%' },
      { field: 'lastName', header: tr('Last name'), width: '25%' },
      { field: 'displayName', header: tr('Name'), width: '30%' },
    ];
  }

  ngOnDestroy(): void {}
}
